import {
	CloseOutlined,
	DollarOutlined,
	DownloadOutlined,
	ExclamationCircleOutlined,
	FileDoneOutlined,
	FileExcelOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import {
	Button,
	Divider,
	Modal,
	Radio,
	Space,
	Spin,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import Column from 'antd/es/table/Column';
import Title from 'antd/es/typography/Title';
import { midMonthPayApi } from 'Api/midMonthPays';
import DownloadComponent from 'components/Global/Download';
import { useContext, useState } from 'react';
import { FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toastText } from 'utils/utils';
import AddModal from '../AddModal';
import './index.scss';
import { PermissionContext } from 'components/Global/AuthLayout';
const { Text } = Typography;

type HistoryProps = {
	open: boolean;
	closeModal: () => void;
	payGroupData: any;
	isLoading: boolean;
	fetchMidMonthData: () => void;
};

const MidMonthPayGroupModal = (props: HistoryProps) => {
	const permissionsContext = useContext(PermissionContext);

	const isAdd =
		permissionsContext.allowedPermissions.includes('Add_Mid-Month_Payment');
	const { open, closeModal, payGroupData, isLoading, fetchMidMonthData } =
		props;

	const navigate = useNavigate();

	const [selectedPayrollCurrency, setSelectedPayrollCurrency] = useState<
		string 
	>('');

	const [selectedPayGroupId, setSelectedPayGroupId] = useState<string >(
		''
	);
	const [isBankErrorModalVisible, setIsBankErrorModalVisible] =
		useState(false);
	const [midMonthDetailsData, setMidMonthDetailsData] = useState([]);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isBankFilesModalVisible, setIsBankFilesModalVisible] =
		useState(false);
	const [bankFileDownloadLoading, setBankFileDownloadLoading] = useState<{
		[key: string]: boolean;
	}>({});
	const [selectedPayrollForBankFiles, setSelectedPayrollForBankFiles] =
		useState<string | null>(null);
	const [exportFormat, setExportFormat] = useState<'CSV' | 'EXCEL'>('CSV');

	const handleModalOpen = (midMonthPayGroupId: string, currency: string) => {
		setSelectedPayGroupId(midMonthPayGroupId);
		if (currency) {
			setSelectedPayrollCurrency(currency);
		}
		showModal(midMonthPayGroupId, currency);
	};

	const showModal = (midMonthPayGroupId: string, currency: string) => {
		fetchBankDetailsForPayment(midMonthPayGroupId, currency);
	};

	const fetchBankDetailsForPayment = async (midMonthPayGroupId: string, currency: string) => {
		try {

			const bankDetails =
				await midMonthPayApi.getBankDetailsForMidMonthDetails({
					currency: currency ,
					midMonthPayGroupId: selectedPayGroupId,
				});
				console.log(bankDetails);

			const details = bankDetails?.data?.data || [];

			if (details.length === 0) {
				setIsBankErrorModalVisible(true);
				return;
			}

			setMidMonthDetailsData(bankDetails.data.data);
			setIsModalVisible(true);
		} catch (error: any) {
			toastText(
				error.response?.data?.message ??
				'Something went wrong in fetching mid-month details',
				'error'
			);
		}
	};

	const handleRedirect = () => {
		handleErrorModal();
		navigate('/settings/configurations/company-setup?tab=bankDetails');
	};

	const handleErrorModal = () => {
		setIsBankErrorModalVisible(false);
	};

	const handleBankFileDownload = async (
		payGroupId: string,
		bankType: string,
		fileType: 'csv' | 'excel' = 'csv'
	) => {
		try {
			// Include fileType in the loading key
			const loadingKey = `${payGroupId}_${bankType}_${fileType}`;
			setBankFileDownloadLoading((prev) => ({
				...prev,
				[loadingKey]: true,
			}));

			const midMonth: any = payGroupData.find(
				(data: any) => data.payGroupId === payGroupId
			);

			const response = await midMonthPayApi.exportMidMonthBank(
				midMonth!.midMonthPayId,
				payGroupId,
				bankType,
				fileType
			);

			// Determine filename based on bank type and file type
			const bankName = bankType.replace(/_/g, '_').toLowerCase();
			const fileExtension = fileType === 'excel' ? 'xlsx' : 'csv';
			const fileName = `${midMonth?.payGroupName}_${bankName}_Midmonth_bank_file.${fileExtension}`;

			// Set appropriate MIME type based on file type
			const mimeType =
				fileType === 'excel'
					? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
					: 'text/csv';

			const blob = new Blob([response.data], { type: mimeType });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Clean up the URL object
			URL.revokeObjectURL(url);

			// Success message with bank type and file type
			toastText(
				`${bankType.replace(
					/_/g,
					' '
				)} ${fileType.toLowerCase()} file downloaded successfully`,
				'success'
			);
		} catch (error: any) {
			let message = `Something went wrong in fetching ${fileType} file`;

			if (error?.response?.data?.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			// Include fileType in the loading key for cleanup
			const loadingKey = `${payGroupId}_${bankType}_${fileType}`;
			setBankFileDownloadLoading((prev) => ({
				...prev,
				[loadingKey]: false,
			}));
		}
	};

	const handleOpenBankFilesModal = (payGroupId: string) => {
		setSelectedPayrollForBankFiles(payGroupId);
		setIsBankFilesModalVisible(true);
	};

	// const handleBankFileDownloadFromModal = async (
	// 	bankType: string,
	// 	bankName: string
	// ) => {
	// 	if (selectedPayrollForBankFiles) {
	// 		await handleBankFileDownload(selectedPayrollForBankFiles, bankType, exportFormat);
	// 	}
	// };

	const handleCloseBankFilesModal = () => {
		setIsBankFilesModalVisible(false);
		setSelectedPayrollForBankFiles(null);
	};
	return (
		<>
			<Modal
				// title="History"
				open={open}
				// onCancel={closeModal}
				maskClosable={true}
				width={1000}
				closable={false}
				footer={null}
				rootClassName="napsaModalRoot"
			>
				<div className="modal-header">
					<Title level={4} className="modal-title">
						Pay Group Details
					</Title>
					<div className="close-icon" onClick={closeModal}>
						<CloseOutlined />
					</div>
				</div>

				<Table
					dataSource={payGroupData}
					pagination={false}
					rowKey={(record) => record.id}
					loading={isLoading}
				>
					<Column
						title="Pay Group Name"
						dataIndex="payGroupName"
						key="payGroupName"
						sorter={true}
						className="bg-white"
					/>
					<Column
						title="Total Employees"
						key="totalEmployees"
						dataIndex="totalEmployees"
						className="bg-white"
					/>
					<Column
						title="Total Amount"
						key="totalAmount"
						dataIndex="totalAmount"
						className="bg-white"
					/>
					<Column
						title="Bank File"
						dataIndex="bankFile"
						key="bankFile"
						width={100}
						align="center"
						sorter={false}
						className="bg-white"
						render={(_, record: any) => (
							<Tooltip title="Click to download bank files.">
								<FileDoneOutlined
									style={{
										fontSize: '19px',
										cursor: 'pointer',
									}}
									onClick={() => {
										handleOpenBankFilesModal(
											record.payGroupId
										);
									}}
								/>
							</Tooltip>
						)}
					/>

					{/* 
					<Column
						title="File"
						dataIndex="documentUrl"
						className="bg-white"
						key="documentUrl"
						width={'10%'}
						render={(text, record: any) => (
							<div>
								<DownloadComponent
									documentUrl={record.attachmentUrl}
									icon={
										<FaFileExcel
											color="#217346"
											size={20}
										/>
									}
									showTooltip={true}
									isLink={false}
									downloadText={
										record.documentName.split('.')[0]
									}
									tooltipText={
										record.documentName.split('.')[0]
									}
									maxLength={20}
								/>
							</div>
						)}
					/> */}
					{isAdd &&
						<Column
							title="Initiate Payment"
							dataIndex="initiatePayment"
							key="initiatePayment"
							width={170}
							align="center"
							sorter={false}
							className="bg-white"
							render={(_, record: any) => {
								const isDisabled =
									record.status !== 'FILE_GENERATED';

								return (
									<Tooltip
										title={
											isDisabled
												? 'Payment initiation is available when status of file is generated and with right permissions'
												: 'Click to initiate payment'
										}
									>
										{!isDisabled ? (
											<div
												onClick={() => {
													handleModalOpen(record.id, record.currency);
												}}
												style={{
													cursor: 'pointer',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '19px',
													width: '100%',
													height: '100%',
												}}
											>
												<DollarOutlined
												// style={{
												// 	color: '#584495',
												// }}
												/>
											</div>
										) : (
											<div className="text-gray-400">N/A</div>
										)}
									</Tooltip>
								);
							}}
						/>
					}
				</Table>
			</Modal>
			<AddModal
				isOpen={isModalVisible}
				handleCancel={() => {
					setIsModalVisible(false);
					setSelectedPayGroupId('');
				}}
				isBankLoading={isLoading}
				bankDetailsData={midMonthDetailsData}
				midMonthPayGroupId={selectedPayGroupId || ''}
				handleBankDetails={setMidMonthDetailsData}
				currency={selectedPayrollCurrency || ''}
				fetchMidMonthData={fetchMidMonthData}
			/>
			<Modal
				open={isBankErrorModalVisible}
				onCancel={() => setIsBankErrorModalVisible(false)}
				footer={null}
				centered
				width={520}
				closable={false}
			>
				<div style={{ textAlign: 'center', padding: '10px 5px' }}>
					<div
						style={{
							width: 70,
							height: 70,
							borderRadius: '50%',
							background: '#FFF7E6',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							margin: '0 auto',
						}}
					>
						<ExclamationCircleOutlined
							style={{ fontSize: 38, color: '#faad14' }}
						/>
					</div>

					<h2 style={{ marginTop: 16, fontWeight: 700 }}>
						Bank Details Required
					</h2>

					<p
						style={{
							fontSize: 15,
							color: '#595959',
							marginTop: 8,
							lineHeight: 1.6,
						}}
					>
						To process payments, please add and verify a bank
						account in the Company Setup section.
					</p>

					<div
						style={{
							textAlign: 'left',
							background: '#fafafa',
							padding: '16px 20px',
							borderRadius: 8,
							marginTop: 24,
							border: '1px solid #f0f0f0',
						}}
					>
						<p
							style={{
								fontWeight: 600,
								marginBottom: 10,
								color: '#444',
							}}
						>
							Bank must meet the following requirements:
						</p>

						<ul
							style={{
								paddingLeft: 20,
								lineHeight: 1.6,
								margin: 0,
							}}
						>
							<li>
								Bank Type: <b>First Capital Bank</b>
							</li>
							<li>
								Account status: <b>Verified</b>
							</li>
							<li>
								Currency:{' '}
								<span
									style={{
										background: '#e6f7ff',
										padding: '2px 6px',
										borderRadius: 4,
										fontWeight: 600,
									}}
								>
									{selectedPayrollCurrency || 'ZMW'}
								</span>
							</li>
							<li>Fund Transfer must be enabled</li>
						</ul>
					</div>

					<Button
						type="primary"
						size="large"
						style={{ marginTop: 26, borderRadius: 6 }}
						onClick={handleRedirect}
					>
						Go to Company Setup
					</Button>
				</div>
			</Modal>

			{isBankFilesModalVisible && (
				<Modal
					open={isBankFilesModalVisible}
					onCancel={handleCloseBankFilesModal}
					footer={null}
					closeIcon={null}
					width={600}
					styles={{
						body: { padding: 0 },
					}}
				>
					{/* Custom Header */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '5px 0px 8px 10px',
						}}
					>
						<Text strong style={{ fontSize: '20px' }}>
							Multi-Bank Payroll Files
						</Text>
						<Button
							type="text"
							icon={<CloseOutlined />}
							onClick={handleCloseBankFilesModal}
							style={{ marginRight: '-8px' }}
						/>
					</div>

					{/* Modal Content */}
					<div style={{ padding: '16px 12px' }}>
						{/* Note */}
						<div
							style={{
								backgroundColor: '#f4f1ff',
								paddingInline: '14px',
								paddingBlock: '8px',
								borderRadius: '6px',
								marginBottom: '12px',
								border: '1px solid #584495',
							}}
						>
							<Text
								style={{ fontSize: '14px', color: '#584495' }}
							>
								Download these files for manual payment via the
								bank portals
							</Text>
						</div>

						{/* Format Selection */}
						{/* <div
							style={{
								marginBottom: '16px',
								padding: '12px',
								backgroundColor: '#fafafa',
								borderRadius: '6px',
								border: '1px solid #f0f0f0',
							}}
						>
							<Text strong style={{ marginRight: '12px' }}>
								Export Format:
							</Text>
							<Radio.Group
								value={exportFormat}
								onChange={(e) => setExportFormat(e.target.value)}
								buttonStyle="solid"
							>
								<Radio.Button value="CSV">CSV</Radio.Button>
								<Radio.Button value="EXCEL">Excel</Radio.Button>
							</Radio.Group>
						</div> */}

						{/* Bank List */}
						<Space
							direction="vertical"
							style={{ width: '100%' }}
							size="middle"
						>
							{[
								{
									id: 'stanbic',
									name: 'Stanbic Bank',
									type: 'STANBIC_BANK',
									logo: 'assets/images/Stanbic-Bank-Logo.jpg',
								},
								{
									id: 'access',
									name: 'Access Bank',
									type: 'ACCESS_BANK',
									logo: 'assets/images/access-bank.png',
								},
								{
									id: 'indo-zam',
									name: 'Indo-Zam Bank',
									type: 'INDO_ZAMBIA_BANK',
									logo: 'assets/images/indo-zam-bank.jpg',
								},
								{
									id: 'zanaco',
									name: 'Zanaco Bank',
									type: 'ZANACO',
									logo: 'assets/images/zanaco-bank.jpg',
								},
							].map((bank, index) => {
								const csvLoadingKey = `${selectedPayrollForBankFiles}_${bank.type}_csv`;
								const excelLoadingKey = `${selectedPayrollForBankFiles}_${bank.type}_excel`;

								const isCsvLoading =
									bankFileDownloadLoading[csvLoadingKey];
								const isExcelLoading =
									bankFileDownloadLoading[excelLoadingKey];


								return (
									<div key={bank.id}>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												padding: '12px 0',
											}}
										>
											{/* Bank Info with Logo */}
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '12px',
												}}
											>
												<img
													src={bank.logo}
													alt={`${bank.name} logo`}
													style={{
														width: '70px',
														height: '50px',
														objectFit: 'contain',
														borderRadius: '4px',
													}}
												/>
												<Text
													style={{
														fontSize: '15px',
														fontWeight: 500,
													}}
												>
													{bank.name}
												</Text>
											</div>

											<Space size="small">
												<Tooltip title="Download CSV">
													<Button
														type="default"
														icon={
															isCsvLoading ? (
																<Spin size="small" />
															) : (
																<FileTextOutlined />
															)
														}
														onClick={() =>
															handleBankFileDownload(
																selectedPayrollForBankFiles!,
																bank.type,
																'csv'
															)
														}
														loading={isCsvLoading}
														disabled={
															isExcelLoading
														}
														style={{
															minWidth: '90px',
														}}
													>
														CSV
													</Button>
												</Tooltip>

												<Tooltip title="Download Excel">
													<Button
														type="primary"
														icon={
															isExcelLoading ? (
																<Spin size="small" />
															) : (
																<FileExcelOutlined />
															)
														}
														onClick={() =>
															handleBankFileDownload(
																selectedPayrollForBankFiles!,
																bank.type,
																'excel'
															)
														}
														loading={isExcelLoading}
														disabled={isCsvLoading}
														style={{
															minWidth: '90px',
														}}
													>
														Excel
													</Button>
												</Tooltip>
											</Space>
										</div>
										{index < 2 && (
											<Divider style={{ margin: 0 }} />
										)}
									</div>
								);
							})}
						</Space>
					</div>
				</Modal>
			)}
		</>
	);
};

export default MidMonthPayGroupModal;
